import { logger } from '@/utils/logger'

export interface AuthExpiredAction {
  text: string
  handler: () => void
}

export interface AuthExpiredOptions {
  title: string
  content: string
  action?: AuthExpiredAction
  duration?: number
  onClose?: () => void
}

export interface IMessageService {
  success(message: string): void
  error(message: string): void
  warning(message: string | AuthExpiredOptions): void
  info(message: string): void
  
  // API Loading - 通知式
  apiLoading(message: string, completedMessage?: string): () => void
  
  // Form Loading - 遮罩式
  formLoading(message: string, completedMessage?: string): () => void
  
  // 向後相容的 loading 方法
  loading(message: string, completedMessage?: string): () => void
  
  // 認證過期提示
  authExpired(options: AuthExpiredOptions): void
}

export class MessageService implements IMessageService {
  private messageApi: any = null
  private loadingBarApi: any = null
  private readonly enableConsole: boolean
  private initPromise: Promise<void> | null = null

  constructor(enableConsole: boolean = false) {
    this.enableConsole = enableConsole
  }

  private async initMessageApi(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise
    }
    
    this.initPromise = (async () => {
      try {
        const { createDiscreteApi } = await import('naive-ui')
        const { message, loadingBar } = createDiscreteApi(['message', 'loadingBar'])
        this.messageApi = message
        this.loadingBarApi = loadingBar
      } catch (error) {
        logger.warn('Failed to initialize Naive UI message API', error)
      }
    })()
    
    return this.initPromise
  }

  private async ensureInitialized(): Promise<void> {
    await this.initMessageApi()
  }

  private logToConsole(type: string, message: string) {
    if (!this.enableConsole) return

    const timestamp = new Date().toISOString()
    switch (type) {
      case 'success':
        logger.info(`[SUCCESS] ${timestamp} - ${message}`)
        break
      case 'error':
        logger.error(`[ERROR] ${timestamp} - ${message}`)
        break
      case 'warning':
        logger.warn(`[WARNING] ${timestamp} - ${message}`)
        break
      case 'info':
        logger.info(`[INFO] ${timestamp} - ${message}`)
        break
      case 'loading':
        logger.info(`[LOADING] ${timestamp} - ${message}`)
        break
      case 'completed':
        logger.info(`[COMPLETED] ${timestamp} - ${message}`)
        break
    }
  }

  success(message: string): void {
    // 異步顯示 UI 訊息（不阻塞執行）
    this.ensureInitialized().then(() => {
      if (this.messageApi) {
        this.messageApi.success(message)
      }
    })
    // 條件式 console 輸出（同步執行）
    this.logToConsole('success', message)
  }

  error(message: string): void {
    // 異步顯示 UI 訊息（不阻塞執行）
    this.ensureInitialized().then(() => {
      if (this.messageApi) {
        this.messageApi.error(message)
      }
    })
    // 條件式 console 輸出（同步執行）
    this.logToConsole('error', message)
  }

  warning(message: string | AuthExpiredOptions): void {
    if (typeof message === 'string') {
      // 原有的簡單 warning 訊息
      this.ensureInitialized().then(() => {
        if (this.messageApi) {
          this.messageApi.warning(message)
        }
      })
      this.logToConsole('warning', message)
    } else {
      // 進階的認證過期訊息
      this.authExpired(message)
    }
  }

  info(message: string): void {
    // 異步顯示 UI 訊息（不阻塞執行）
    this.ensureInitialized().then(() => {
      if (this.messageApi) {
        this.messageApi.info(message)
      }
    })
    // 條件式 console 輸出（同步執行）
    this.logToConsole('info', message)
  }

  loading(message: string, completedMessage?: string): () => void {
    // 條件式 console 輸出（同步執行）
    this.logToConsole('loading', message)
    
    // 追蹤是否已被銷毀
    let isDestroyed = false
    let messageReactive: any = null // 參考 naive-ui 官方模式
    let initializationPromise: Promise<void>
    
    // 異步顯示 UI 載入訊息並保存 Promise
    initializationPromise = this.ensureInitialized().then(() => {
      if (this.messageApi && !isDestroyed) {
        messageReactive = this.messageApi.loading(message)
      }
    }).catch(() => {
      // 初始化失敗時不做任何事
    })
    
    // 回傳安全的清理函數
    return () => {
      isDestroyed = true
      this.logToConsole('completed', completedMessage ?? message)
      
      // 等待初始化完成後再清理
      initializationPromise.then(() => {
        if (messageReactive) {
          try {
            messageReactive.destroy()
            messageReactive = null // 重要：清空引用避免重複操作
          } catch (error) {
            logger.warn('Error destroying loading message', error)
          }
        }
        
        // 如果有完成訊息，顯示成功訊息
        if (completedMessage) {
          this.success(completedMessage)
        }
      }).catch(() => {
        // 如果初始化失敗，至少顯示完成訊息
        if (completedMessage) {
          this.success(completedMessage)
        }
      })
    }
  }

  /**
   * API Loading - 用於資料載入等非阻塞操作
   * 顯示在通知區域，不阻止頁面其他操作
   */
  apiLoading(message: string, completedMessage?: string): () => void {
    this.logToConsole('api-loading', message)
    
    let isDestroyed = false
    let messageReactive: any = null // 參考 naive-ui 官方模式
    let initializationPromise: Promise<void>
    
    // 初始化並保存 Promise
    initializationPromise = this.ensureInitialized().then(() => {
      if (this.messageApi && !isDestroyed) {
        messageReactive = this.messageApi.loading(message, {
          duration: 0, // 持續顯示直到手動關閉
          keepAliveOnHover: true
        })
      }
    }).catch(() => {
      // 初始化失敗時不做任何事
    })
    
    return () => {
      isDestroyed = true
      this.logToConsole('api-completed', completedMessage ?? message)
      
      // 等待初始化完成後再清理
      initializationPromise.then(() => {
        if (messageReactive) {
          try {
            messageReactive.destroy()
            messageReactive = null // 重要：清空引用避免重複操作
          } catch (error) {
            logger.warn('Error destroying API loading message', error)
          }
        }
        
        // 顯示完成訊息
        if (completedMessage) {
          // 短暫延遲確保 loading 已清理
          setTimeout(() => {
            this.success(completedMessage)
          }, 100)
        }
      }).catch(() => {
        // 如果初始化失敗，至少顯示完成訊息
        if (completedMessage) {
          this.success(completedMessage)
        }
      })
    }
  }

  /**
   * Form Loading - 用於表單操作等阻塞操作
   * 使用遮罩式 loading，阻止使用者其他操作
   */
  formLoading(message: string, completedMessage?: string): () => void {
    this.logToConsole('form-loading', message)
    
    let isDestroyed = false
    let messageReactive: any = null // 參考 naive-ui 官方模式
    
    // 使用 loadingBar 和 message 組合實現遮罩效果
    const loadingPromise = this.ensureInitialized().then(() => {
      if (!isDestroyed) {
        // 啟動進度條
        if (this.loadingBarApi) {
          this.loadingBarApi.start()
        }
        
        // 顯示 loading 訊息
        if (this.messageApi) {
          messageReactive = this.messageApi.loading(message, {
            duration: 0,
            keepAliveOnHover: true,
            closable: false // 表單 loading 不允許手動關閉
          })
        }
        
        // 添加全域遮罩
        this.addGlobalMask()
        
        return messageReactive
      }
      return null
    }).catch(() => null)
    
    return () => {
      isDestroyed = true
      this.logToConsole('form-completed', completedMessage ?? message)
      
      loadingPromise.then((instance) => {
        // 移除 loading 訊息
        if (instance) {
          try {
            instance.destroy()
            messageReactive = null // 重要：清空引用避免重複操作
          } catch (error) {
            logger.warn('Error destroying form loading message', error)
          }
        }
        
        // 完成進度條
        if (this.loadingBarApi) {
          this.loadingBarApi.finish()
        }
        
        // 移除全域遮罩
        this.removeGlobalMask()
        
        // 顯示完成訊息
        if (completedMessage) {
          setTimeout(() => {
            this.success(completedMessage)
          }, 200)
        }
      })
    }
  }

  /**
   * 添加全域遮罩（用於表單 loading）
   */
  private addGlobalMask(): void {
    // 創建遮罩元素
    const mask = document.createElement('div')
    mask.id = 'form-loading-mask'
    mask.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(2px);
      z-index: 9999;
      cursor: wait;
      pointer-events: auto;
    `
    
    // 防止滾動
    document.body.style.overflow = 'hidden'
    document.body.appendChild(mask)
  }

  /**
   * 移除全域遮罩
   */
  private removeGlobalMask(): void {
    const mask = document.getElementById('form-loading-mask')
    if (mask) {
      document.body.removeChild(mask)
    }
    // 恢復滾動
    document.body.style.overflow = ''
  }

  /**
   * 認證過期友善提示
   */
  authExpired(options: AuthExpiredOptions): void {
    this.logToConsole('auth-expired', options.content)

    this.ensureInitialized().then(() => {
      if (this.messageApi) {
        // 創建帶有操作按鈕的警告訊息
        const messageReactive = this.messageApi.warning(options.content, {
          duration: options.duration || 0, // 不自動消失
          keepAliveOnHover: true,
          closable: true,
          onClose: () => {
            if (options.onClose) {
              options.onClose()
            }
          },
          // 使用 render 函數添加自定義按鈕 (如果支援的話)
          ...(options.action && {
            action: () => {
              // 創建按鈕元素
              const button = document.createElement('button')
              button.textContent = options.action!.text
              button.style.cssText = `
                margin-left: 8px;
                padding: 4px 12px;
                background-color: #18a058;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
              `
              button.onclick = () => {
                messageReactive.destroy()
                options.action!.handler()
              }
              return button
            }
          })
        })

        // 如果 naive-ui 不支援自定義 action，使用替代方案
        if (options.action) {
          // 延遲一點顯示額外提示
          setTimeout(() => {
            const actionMessage = this.messageApi.info(`點擊此處 → ${options.action!.text}`, {
              duration: 10000,
              closable: true,
              onClose: () => {
                options.action!.handler()
              }
            })
          }, 1000)
        }
      } else {
        // 降級到 console 和 alert
        console.warn(`🔒 ${options.title}: ${options.content}`)
        if (options.action) {
          const userConfirmed = confirm(`${options.content}\n\n是否要${options.action.text}？`)
          if (userConfirmed) {
            options.action.handler()
          }
        }
      }
    }).catch(() => {
      // 完全降級到原生對話框
      const userConfirmed = confirm(`${options.title}\n\n${options.content}\n\n是否要${options.action?.text || '繼續'}？`)
      if (userConfirmed && options.action) {
        options.action.handler()
      }
    })
  }
}

// 根據環境變數決定是否啟用 console 日誌
const enableConsoleLogging = 
  import.meta.env.VITE_ENABLE_MESSAGE_CONSOLE === 'true'

// 統一匯出單例
export const messageService: IMessageService = new MessageService(enableConsoleLogging)
